import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, Tag, SlidersHorizontal, X, PenLine } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { translations } from '../translations/translations';
import { fadeUp, staggerContainer } from '../motion/presets';
import '../styles/BlogList.css';

function formatDate(dateStr, language) {
    try {
        return new Date(dateStr).toLocaleDateString(
            language === 'tr' ? 'tr-TR' : 'en-US',
            { year: 'numeric', month: 'long', day: 'numeric' }
        );
    } catch {
        return dateStr;
    }
}

function EmptyState({ blogLang }) {
    return (
        <motion.div
            className="blog-empty-state"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
            <div className="blog-empty-state__icon-wrap">
                <PenLine size={28} strokeWidth={1.5} />
            </div>
            <h2 className="blog-empty-state__title">
                {blogLang === 'tr' ? 'Henüz yazı yok' : 'Nothing here yet'}
            </h2>
            <p className="blog-empty-state__subtitle">
                {blogLang === 'tr'
                    ? 'Türkçe yazılar yakında burada olacak.'
                    : 'English posts are coming soon.'}
            </p>
            <div className="blog-empty-state__line" />
            <span className="blog-empty-state__hint">
                {blogLang === 'tr' ? '— Mert' : '— Mert'}
            </span>
        </motion.div>
    );
}

export default function BlogListPage() {
    const [posts, setPosts] = useState([]);
    const [activeCategory, setActiveCategory] = useState('All');
    const [blogLang, setBlogLang] = useState('tr');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const sidebarRef = useRef(null);
    const { language } = useAppContext();
    const navigate = useNavigate();
    // t always follows the blog content language, not the app UI language
    const t = translations[blogLang];

    useEffect(() => {
        fetch(`${process.env.PUBLIC_URL}/blog/posts.json`)
            .then((r) => r.json())
            .then((data) => setPosts(data))
            .catch(() => setPosts([]));
    }, []);

    // Reset category filter when switching blog language
    useEffect(() => {
        setActiveCategory('All');
        setSidebarOpen(false);
    }, [blogLang]);

    // Close sidebar on click outside
    useEffect(() => {
        function handleClickOutside(e) {
            if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
                setSidebarOpen(false);
            }
        }
        if (sidebarOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [sidebarOpen]);

    const langFiltered = posts.filter((p) => p.lang === blogLang);
    const categories = ['All', ...Array.from(new Set(langFiltered.map((p) => p.category)))];
    const filtered = activeCategory === 'All' ? langFiltered : langFiltered.filter((p) => p.category === activeCategory);
    const isEmpty = langFiltered.length === 0;

    return (
        <div className="blog-list-page">
            {/* ── Minimal Header ── */}
            <header className="blog-list-header">
                <div className="blog-list-header__inner">
                    <button className="blog-list-back" onClick={() => navigate('/')} aria-label="Back to portfolio">
                        <ArrowLeft size={16} />
                        <span>{blogLang === 'tr' ? 'Portfolyo' : 'Portfolio'}</span>
                    </button>

                    {/* Language toggle */}
                    <div className="blog-lang-toggle">
                        <button
                            className={`blog-lang-toggle__btn ${blogLang === 'tr' ? 'blog-lang-toggle__btn--active' : ''}`}
                            onClick={() => setBlogLang('tr')}
                        >
                            TR
                        </button>
                        <span className="blog-lang-toggle__sep" />
                        <button
                            className={`blog-lang-toggle__btn ${blogLang === 'en' ? 'blog-lang-toggle__btn--active' : ''}`}
                            onClick={() => setBlogLang('en')}
                        >
                            EN
                        </button>
                    </div>

                    <img
                        src={`${process.env.PUBLIC_URL}/favicon.svg`}
                        alt="Mert Özkaya"
                        width="36"
                        height="36"
                        style={{ display: 'block' }}
                    />
                </div>
            </header>

            {/* ── Category Sidebar Toggle (only when posts exist) ── */}
            {!isEmpty && (
                <div className="blog-sidebar-wrap" ref={sidebarRef}>
                    <button
                        className={`blog-sidebar-toggle ${sidebarOpen ? 'blog-sidebar-toggle--open' : ''}`}
                        onClick={() => setSidebarOpen((v) => !v)}
                        aria-label="Filter by category"
                        aria-expanded={sidebarOpen}
                    >
                        {sidebarOpen ? <X size={16} /> : <SlidersHorizontal size={16} />}
                    </button>

                    <AnimatePresence>
                        {sidebarOpen && (
                            <motion.div
                                className="blog-sidebar-panel"
                                initial={{ opacity: 0, x: -12, scale: 0.96 }}
                                animate={{ opacity: 1, x: 0, scale: 1 }}
                                exit={{ opacity: 0, x: -12, scale: 0.96 }}
                                transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                            >
                                <p className="blog-sidebar-panel__label">
                                    {blogLang === 'tr' ? 'Kategori' : 'Category'}
                                </p>
                                <ul className="blog-sidebar-panel__list">
                                    {categories.map((cat) => (
                                        <li key={cat}>
                                            <button
                                                className={`blog-sidebar-panel__item ${activeCategory === cat ? 'blog-sidebar-panel__item--active' : ''}`}
                                                onClick={() => { setActiveCategory(cat); setSidebarOpen(false); }}
                                            >
                                                {cat === 'All' ? (blogLang === 'tr' ? 'Tümü' : 'All') : cat}
                                                {activeCategory === cat && (
                                                    <motion.span className="blog-sidebar-panel__dot" layoutId="catDot" />
                                                )}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            )}

            {/* ── Main Content ── */}
            <main className="blog-list-main">
                {/* Page Title */}
                <motion.div
                    className="blog-list-hero"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                >
                    <span className="blog-list-hero__label">Blog</span>
                    <h1 className="blog-list-hero__title">{t.blog.title}</h1>
                    <p className="blog-list-hero__subtitle">{t.blog.subtitle}</p>

                    {activeCategory !== 'All' && (
                        <div className="blog-list-filter-indicator">
                            <Tag size={12} />
                            <span>{activeCategory}</span>
                            <button onClick={() => setActiveCategory('All')} aria-label="Clear filter">
                                <X size={12} />
                            </button>
                        </div>
                    )}
                </motion.div>

                {/* Empty state */}
                <AnimatePresence mode="wait">
                    {isEmpty ? (
                        <EmptyState key={`empty-${blogLang}`} blogLang={blogLang} />
                    ) : (
                        <motion.div
                            key={`grid-${blogLang}-${activeCategory}`}
                            className="blog-list-grid"
                            variants={staggerContainer(0.08, 0.1)}
                            initial="hidden"
                            animate="visible"
                            exit={{ opacity: 0 }}
                        >
                            {filtered.map((post) => (
                                <motion.article
                                    key={post.id}
                                    className="blog-card"
                                    variants={fadeUp}
                                    layout
                                >
                                    <Link to={`/blog/${post.id}`} className="blog-card__link" aria-label={post.title}>
                                        {post.coverImage && (
                                            <div className="blog-card__cover">
                                                <img
                                                    src={post.coverImage.startsWith('http') ? post.coverImage : `${process.env.PUBLIC_URL}${post.coverImage}`}
                                                    alt={post.title}
                                                    className="blog-card__cover-img"
                                                    loading="lazy"
                                                />
                                            </div>
                                        )}
                                        <div className="blog-card__meta">
                                            <span className="blog-card__category">
                                                <Tag size={10} />
                                                {post.category}
                                            </span>
                                            <span className="blog-card__date">
                                                <Calendar size={10} />
                                                {formatDate(post.date, language)}
                                            </span>
                                            <span className="blog-card__read-time">
                                                <Clock size={10} />
                                                {post.readTime}
                                            </span>
                                        </div>

                                        <h2 className="blog-card__title">{post.title}</h2>
                                        <p className="blog-card__excerpt">{post.excerpt}</p>

                                        <span className="blog-card__cta">
                                            {t.blog.readMore}
                                            <span className="blog-card__cta-arrow">→</span>
                                        </span>
                                    </Link>
                                </motion.article>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Category empty (when a category has no posts but lang does) */}
                {!isEmpty && filtered.length === 0 && (
                    <motion.p
                        className="blog-list-empty"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                    >
                        {blogLang === 'tr' ? 'Bu kategoride yazı bulunamadı.' : 'No posts in this category.'}
                    </motion.p>
                )}
            </main>
        </div>
    );
}
